/**
 * Recently visited pages for the ⌘K palette. Stored as an immutable list of
 * {label, path} pairs in localStorage — newest first, deduped, capped. The
 * label is captured at visit time so detail pages (an env, a capture run) keep
 * a distinguishing name instead of collapsing to their parent nav item at
 * display time.
 */
import { navItemForPath } from './nav-config'

const STORAGE_KEY = 'it.recentPages'
const MAX_RECENT = 5
const IGNORED = new Set(['/', '/login', '/orgs'])

export interface RecentPage {
  label: string
  path: string
}

/**
 * Readable label for a path at visit time — the matched nav item's label, with
 * the trailing detail segment appended so sibling detail pages don't all
 * collapse to the same parent label in the Recent list.
 */
export function labelForPath(pathname: string): string {
  const item = navItemForPath(pathname)
  const base = item?.label ?? pathname
  const roots = item ? [item.to, ...(item.also ?? [])] : []
  if (roots.some((r) => pathname.startsWith(`${r}/`))) {
    const seg = pathname.split('/').filter(Boolean).pop()
    if (seg) return `${base} · ${seg}`
  }
  return base
}

export function getRecentPages(): readonly RecentPage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (p): p is RecentPage =>
        typeof p === 'object' &&
        p !== null &&
        typeof (p as RecentPage).path === 'string' &&
        typeof (p as RecentPage).label === 'string',
    )
  } catch {
    return []
  }
}

export function recordRecentPage(page: RecentPage): void {
  if (IGNORED.has(page.path)) return
  const next = [page, ...getRecentPages().filter((p) => p.path !== page.path)].slice(0, MAX_RECENT)
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Best-effort — the palette just shows fewer recents.
  }
}
